import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatAmount } from '@/lib/utils'
import type { DayOfWeekStat } from '@/lib/statistics'

type Props = { stats: DayOfWeekStat[] }

export function WeekdayCard({ stats }: Props) {
  const max = stats.reduce((m, s) => Math.max(m, s.avgPerOccurrence), 0)
  if (max === 0) return null

  const weekdayAvg =
    stats.filter((s) => s.dow <= 5).reduce((sum, s) => sum + s.avgPerOccurrence, 0) / 5
  const weekendAvg =
    stats.filter((s) => s.dow >= 6).reduce((sum, s) => sum + s.avgPerOccurrence, 0) / 2

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">曜日別の平均支出</CardTitle>
        <p className="text-xs text-muted-foreground">同じ曜日が複数回ある場合は1回あたりの平均</p>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-1.5" style={{ height: 110 }}>
          {stats.map((s) => {
            const heightPct = max > 0 ? (s.avgPerOccurrence / max) * 100 : 0
            const isWeekend = s.dow >= 6
            return (
              <div key={s.dow} className="flex h-full flex-1 flex-col items-center">
                <div className="flex w-full flex-1 items-end">
                  <div
                    className="w-full rounded-t-sm"
                    style={{
                      height: `${Math.max(heightPct, 2)}%`,
                      backgroundColor: isWeekend
                        ? 'var(--primary)'
                        : 'color-mix(in oklab, var(--muted-foreground) 45%, transparent)',
                    }}
                    aria-label={`${s.label}: ${formatAmount(s.avgPerOccurrence)}`}
                  />
                </div>
                <div
                  className={`mt-1 text-[10px] tabular-nums ${
                    isWeekend ? 'font-medium text-foreground' : 'text-muted-foreground'
                  }`}
                >
                  {s.label}
                </div>
              </div>
            )
          })}
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
          <div className="rounded-md bg-muted/50 px-2 py-1.5">
            <div className="text-[10px] text-muted-foreground">平日平均</div>
            <div className="font-medium tabular-nums">
              {formatAmount(Math.round(weekdayAvg))}
            </div>
          </div>
          <div className="rounded-md bg-muted/50 px-2 py-1.5">
            <div className="text-[10px] text-muted-foreground">週末平均</div>
            <div className="font-medium tabular-nums">
              {formatAmount(Math.round(weekendAvg))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
